import React, { useState, useEffect, useRef } from 'react';
import './Chatbot.css';

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [phase, setPhase] = useState('contact'); // contact, email-verify, first-question, active, timeout
  const [timeRemaining, setTimeRemaining] = useState(120); // Start with 2 minutes
  const [timerActive, setTimerActive] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [userInfo, setUserInfo] = useState({ name: '', email: '', company: '', phone: '' });
  const [verificationError, setVerificationError] = useState('');
  const [showPhoneHint, setShowPhoneHint] = useState(false);
  const [sessionMessageCount, setSessionMessageCount] = useState(0);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const timerRef = useRef(null);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  const sessionStartTimeRef = useRef(null); // Track when 10-minute session starts (AFTER first AI response)
  const firstSessionStartTimeRef = useRef(null); // Track when user FIRST started chatting (for 1-hour limit)
  const warningsShownRef = useRef(new Set());
  const nameInputRef = useRef(null);
  const verificationInputRef = useRef(null);
  const oneHourCheckIntervalRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    // Don't auto-scroll on contact phase to keep welcome message visible
    if (phase !== 'contact') {
      scrollToBottom();
    }
  }, [messages, phase]);

  // Session management - SEPARATE storage for user data vs session data
  const getUserData = (email) => {
    const users = JSON.parse(localStorage.getItem('yuccan_user_data') || '{}');
    return users[email.toLowerCase()] || null;
  };

  const saveUserData = (email, userData) => {
    const users = JSON.parse(localStorage.getItem('yuccan_user_data') || '{}');
    users[email.toLowerCase()] = userData;
    localStorage.setItem('yuccan_user_data', JSON.stringify(users));
  };

  const getSessionData = (email) => {
    const sessions = JSON.parse(localStorage.getItem('yuccan_chat_sessions') || '{}');
    return sessions[email.toLowerCase()] || null;
  };

  const saveSessionData = (email, sessionData) => {
    const sessions = JSON.parse(localStorage.getItem('yuccan_chat_sessions') || '{}');
    sessions[email.toLowerCase()] = {
      ...sessionData,
      lastUpdated: Date.now()
    };
    localStorage.setItem('yuccan_chat_sessions', JSON.stringify(sessions));
  };

  const clearSessionData = (email) => {
    const sessions = JSON.parse(localStorage.getItem('yuccan_chat_sessions') || '{}');
    delete sessions[email.toLowerCase()];
    localStorage.setItem('yuccan_chat_sessions', JSON.stringify(sessions));
  };

  const checkCooldown = (email) => {
    const session = getSessionData(email);
    if (!session || !session.cooldownUntil) return { onCooldown: false };

    if (!session.sessionEndTime) {
      clearSessionData(email);
      return { onCooldown: false };
    }

    const now = Date.now();
    if (now < session.cooldownUntil) {
      const remainingMs = session.cooldownUntil - now;
      const remainingMinutes = Math.ceil(remainingMs / 60000);
      return { onCooldown: true, remainingMinutes };
    }

    clearSessionData(email);
    return { onCooldown: false };
  };

  // Check for expired sessions on mount and submit them
  useEffect(() => {
    const checkExpiredSessions = () => {
      const sessions = JSON.parse(localStorage.getItem('yuccan_chat_sessions') || '{}');
      const userData = JSON.parse(localStorage.getItem('yuccan_user_data') || '{}');
      const now = Date.now();
      const oneHour = 60 * 60 * 1000;

      Object.keys(sessions).forEach(email => {
        const session = sessions[email];

        // Check if session has firstSessionStartTime and it's been > 1 hour
        if (session.firstSessionStartTime &&
            !session.sessionEndTime &&
            !session.cooldownUntil) {

          const elapsed = now - session.firstSessionStartTime;

          if (elapsed >= oneHour && session.messages && session.messages.length > 0) {
            console.log(`Found expired session for ${email} - submitting conversation`);

            // Get user data
            const user = userData[email];
            if (user && user.email) {
              // Build conversation from saved messages
              const conversation = session.messages.map(msg => ({
                sender: msg.sender,
                text: msg.text,
                timestamp: msg.timestamp
              }));

              // Submit to backend
              fetch('/api/chatbot/lead', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  name: user.name,
                  email: user.email,
                  company: user.company || '',
                  phone: user.phone || '',
                  verified: true,
                  conversation: conversation,
                  sessionDuration: session.messageCount || 0,
                  timestamp: new Date().toISOString(),
                  autoSubmitted: true // Flag to indicate this was auto-submitted after 1 hour
                })
              }).then(() => {
                console.log(`Expired session submitted for ${email}`);

                // Clear session and set cooldown
                sessions[email] = {
                  sessionStartTime: null,
                  firstSessionStartTime: null,
                  messageCount: 0,
                  sessionEndTime: now,
                  cooldownUntil: now + oneHour,
                  messages: []
                };
                localStorage.setItem('yuccan_chat_sessions', JSON.stringify(sessions));
              }).catch(err => {
                console.error(`Failed to submit expired session for ${email}:`, err);
              });
            }
          }
        }
      });
    };

    // Check immediately on mount
    checkExpiredSessions();

    // Also check every 5 minutes while page is open
    const expiredCheckInterval = setInterval(checkExpiredSessions, 5 * 60 * 1000);

    return () => clearInterval(expiredCheckInterval);
  }, []);

  // Load reCAPTCHA
  useEffect(() => {
    if (document.querySelector('script[src*="recaptcha/api.js"]')) {
      setRecaptchaLoaded(true);
      return;
    }

    // Skip reCAPTCHA injection during react-snap / headless pre-rendering
    if (navigator.webdriver || window.location.hostname === 'localhost') return;
    const script = document.createElement("script");
    script.src = "https://www.google.com/recaptcha/api.js?render=6Ld5QC0tAAAAACBa4DGyEe6SVLaGCVB8huTeuxFF";
    script.async = true;
    script.defer = true;

    script.onload = () => {
      window.grecaptcha.ready(() => {
        console.log('Chatbot: reCAPTCHA v3 loaded successfully');
        setRecaptchaLoaded(true);
      });
    };

    script.onerror = () => {
      console.error('Chatbot: Failed to load reCAPTCHA script');
    };

    document.body.appendChild(script);
  }, []);

  // 1-hour auto-submit check - runs every minute when session is active
  useEffect(() => {
    if (firstSessionStartTimeRef.current && userInfo.email) {
      oneHourCheckIntervalRef.current = setInterval(() => {
        const elapsed = Date.now() - firstSessionStartTimeRef.current;
        const oneHour = 60 * 60 * 1000;

        if (elapsed >= oneHour) {
          console.log('1-hour limit reached - auto-submitting conversation');

          // Submit conversation to backend
          submitConversationToBackend();

          // Clear session data but keep user data
          clearSessionData(userInfo.email);

          // Clear interval
          if (oneHourCheckIntervalRef.current) {
            clearInterval(oneHourCheckIntervalRef.current);
            oneHourCheckIntervalRef.current = null;
          }

          // Reset session refs
          sessionStartTimeRef.current = null;
          firstSessionStartTimeRef.current = null;
        }
      }, 60000); // Check every minute

      return () => {
        if (oneHourCheckIntervalRef.current) {
          clearInterval(oneHourCheckIntervalRef.current);
          oneHourCheckIntervalRef.current = null;
        }
      };
    }
  }, [firstSessionStartTimeRef.current, userInfo.email]);

  // Open chat
  useEffect(() => {
    if (isOpen && phase === 'contact') {
      const welcomeMessage = {
        id: Date.now(),
        text: "Hi, I'm Banba, welcome to our website. Please let me have your name and contact details to properly address your enquiries.",
        sender: 'bot',
        timestamp: new Date().toISOString()
      };
      setMessages([welcomeMessage]);

      // Start 2-minute timer
      setTimeRemaining(120);
      setTimerActive(true);
    }
  }, [isOpen, phase]);

  // Auto-focus verification input when phase changes
  useEffect(() => {
    if (phase === 'email-verify') {
      setTimeout(() => {
        verificationInputRef.current?.focus();
      }, 500);
    } else if (phase === 'first-question' || phase === 'active') {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 500);
    }
  }, [phase]);

  const handleTimeout = () => {
    setTimerActive(false);
    setPhase('timeout');

    const isActiveSession = sessionStartTimeRef.current !== null;

    const timeoutMessage = {
      id: Date.now(),
      text: isActiveSession
        ? "Your session time has expired. Please chat with me again in an hour!"
        : "Your time to respond has elapsed, please try again when you're ready.",
      sender: 'bot',
      timestamp: new Date().toISOString()
    };

    // Save cooldown ONLY if active session existed
    if (userInfo.email && isActiveSession) {
      const now = Date.now();

      // Submit conversation to backend NOW
      submitConversationToBackend();

      // Save user data permanently
      saveUserData(userInfo.email, {
        name: userInfo.name,
        email: userInfo.email,
        company: userInfo.company,
        phone: userInfo.phone
      });

      // Clear session data but set cooldown
      saveSessionData(userInfo.email, {
        sessionStartTime: null,
        firstSessionStartTime: null,
        messageCount: 0,
        sessionEndTime: now,
        cooldownUntil: now + (60 * 60 * 1000),
        messages: []
      });
    }

    setTimeout(() => {
      setMessages(prev => [...prev, timeoutMessage]);
      setTimeout(() => {
        scrollToBottom();
        setTimeout(() => {
          setIsOpen(false);
          setTimeout(() => {
            setMessages([]);
            setPhase('contact');
            setTimeRemaining(120);
            setTimerActive(false);
            setIsVerified(false);
            setVerificationCode('');
            setUserInfo({ name: '', email: '', company: '', phone: '' });
            setVerificationError('');
            setInputMessage('');
            setSessionMessageCount(0);
            sessionStartTimeRef.current = null;
            firstSessionStartTimeRef.current = null;
            warningsShownRef.current.clear();
          }, 300);
        }, 5000);
      }, 100);
    }, 100);
  };

  // Timer countdown
  useEffect(() => {
    if (timerActive && timeRemaining > 0) {
      timerRef.current = setTimeout(() => {
        setTimeRemaining(timeRemaining - 1);
      }, 1000);
    } else if (timerActive && timeRemaining === 0) {
      handleTimeout();
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timerActive, timeRemaining]);

  const handleClose = () => {
    setTimerActive(false);

    // Save session ONLY if active session exists
    if (userInfo.email && sessionStartTimeRef.current) {
      saveSessionData(userInfo.email, {
        sessionStartTime: sessionStartTimeRef.current,
        firstSessionStartTime: firstSessionStartTimeRef.current,
        messageCount: sessionMessageCount,
        timeRemaining: timeRemaining,
        messages: messages
      });

      // Save user data separately (persists indefinitely)
      saveUserData(userInfo.email, {
        name: userInfo.name,
        email: userInfo.email,
        company: userInfo.company,
        phone: userInfo.phone
      });
    }

    setIsOpen(false);
    setTimeout(() => {
      setMessages([]);
      setPhase('contact');
      setIsVerified(false);
      setVerificationCode('');
      setUserInfo({ name: '', email: '', company: '', phone: '' });
      setVerificationError('');
      setInputMessage('');
    }, 300);
  };

  const handleMinimize = () => {
    setTimerActive(false);

    if (userInfo.email && sessionStartTimeRef.current) {
      saveSessionData(userInfo.email, {
        sessionStartTime: sessionStartTimeRef.current,
        firstSessionStartTime: firstSessionStartTimeRef.current,
        messageCount: sessionMessageCount,
        timeRemaining: timeRemaining,
        messages: messages
      });

      // Save user data separately (persists indefinitely)
      saveUserData(userInfo.email, {
        name: userInfo.name,
        email: userInfo.email,
        company: userInfo.company,
        phone: userInfo.phone
      });
    }

    setIsOpen(false);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return { valid: false, message: 'Please enter a valid email address' };
    }

    const freeProviders = [
      'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com',
      'live.com', 'aol.com', 'icloud.com', 'mail.com',
      'protonmail.com', 'yandex.com', 'zoho.com'
    ];

    const domain = email.split('@')[1]?.toLowerCase();
    if (freeProviders.includes(domain)) {
      return {
        valid: false,
        message: 'Please use your work email address'
      };
    }

    return { valid: true };
  };

  const handleLeadCapture = async (e) => {
    e.preventDefault();

    if (!userInfo.name || !userInfo.email) {
      setVerificationError('Please provide your name and work email');
      return;
    }

    const emailValidation = validateEmail(userInfo.email);
    if (!emailValidation.valid) {
      setVerificationError(emailValidation.message);
      return;
    }

    setVerificationError('');

    // Check cooldown
    const cooldownCheck = checkCooldown(userInfo.email);
    if (cooldownCheck.onCooldown) {
      setVerificationError(`Please wait ${cooldownCheck.remainingMinutes} more minute(s) before chatting again.`);
      return;
    }

    setTimerActive(false);

    // Check if return user
    const verifiedEmails = JSON.parse(localStorage.getItem('yuccan_verified_emails') || '{}');
    const emailKey = userInfo.email.toLowerCase();

    if (verifiedEmails[emailKey]) {
      // Return user - skip verification
      console.log('Return user detected');
      setIsVerified(true);

      // Check for resumable session
      const existingSession = getSessionData(userInfo.email);
      const canResumeSession = existingSession &&
                               existingSession.sessionStartTime &&
                               !existingSession.sessionEndTime &&
                               !existingSession.cooldownUntil;

      if (canResumeSession) {
        // Use SAVED timeRemaining - DON'T recalculate from elapsed
        const remaining = existingSession.timeRemaining || 0;

        if (remaining > 0 && existingSession.messageCount < 10) {
          setTimeRemaining(remaining);
          setSessionMessageCount(existingSession.messageCount || 0);
          sessionStartTimeRef.current = existingSession.sessionStartTime;
          firstSessionStartTimeRef.current = existingSession.firstSessionStartTime;
          setPhase('active');
          setTimerActive(true);
          console.log(`Resumed session with ${remaining}s remaining`);

          // Restore saved messages
          const savedMessages = existingSession.messages || [];
          const firstName = userInfo.name.split(' ')[0];

          setMessages([
            ...savedMessages,
            {
              id: Date.now(),
              text: `Welcome back, ${firstName}! You can continue chatting.`,
              sender: 'bot',
              timestamp: new Date().toISOString()
            }
          ]);
          return;
        }
      }

      // Can't resume - ask first question
      setPhase('first-question');
      setTimeRemaining(120);
      setTimerActive(true);

      const firstName = userInfo.name.split(' ')[0];
      setMessages(prev => [...prev, {
        id: Date.now(),
        text: `Welcome back, ${firstName}! What would you like to know?`,
        sender: 'bot',
        timestamp: new Date().toISOString()
      }]);
      return;
    }

    try {
      if (!window.grecaptcha || !recaptchaLoaded) {
        throw new Error('reCAPTCHA not loaded. Please refresh the page.');
      }

      console.log('Chatbot: Starting reCAPTCHA token generation...');
      const recaptchaToken = await new Promise((resolve, reject) => {
        window.grecaptcha.ready(async () => {
          try {
            const token = await window.grecaptcha.execute('6Ld5QC0tAAAAACBa4DGyEe6SVLaGCVB8huTeuxFF', {action: 'chatbot_submit'});
            console.log('Chatbot: reCAPTCHA token generated:', token ? 'SUCCESS' : 'FAILED');
            resolve(token);
          } catch (error) {
            console.error('Chatbot: reCAPTCHA execution failed:', error);
            reject(error);
          }
        });
      });

      const response = await fetch('/api/chatbot/send-verification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: userInfo.name,
          email: userInfo.email,
          company: userInfo.company,
          phone: userInfo.phone,
          recaptchaToken
        })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setVerificationError(data.message || 'Failed to send verification code');
        setTimerActive(true);
        return;
      }

      setPhase('email-verify');
      setTimeRemaining(120);
      setTimerActive(true);

      const firstName = userInfo.name.split(' ')[0];
      setMessages(prev => [...prev, {
        id: Date.now(),
        text: `Thank you, ${firstName}! We've sent a verification code to ${userInfo.email}. Please check your email and enter the code below.`,
        sender: 'bot',
        timestamp: new Date().toISOString()
      }]);

    } catch (error) {
      console.error('Lead capture error:', error);
      setVerificationError(error.message || 'Network error. Please try again.');
      setTimerActive(true);
    }
  };

  const handleVerification = async (e) => {
    e.preventDefault();

    if (!verificationCode.trim()) {
      setVerificationError('Please enter the verification code');
      return;
    }

    try {
      const response = await fetch('/api/chatbot/verify-code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: userInfo.email,
          code: verificationCode.trim()
        })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setVerificationError(data.message || 'Invalid verification code');
        return;
      }

      setIsVerified(true);
      setVerificationError('');

      // Save email for return users
      const verifiedEmails = JSON.parse(localStorage.getItem('yuccan_verified_emails') || '{}');
      verifiedEmails[userInfo.email.toLowerCase()] = {
        timestamp: Date.now(),
        name: userInfo.name
      };
      localStorage.setItem('yuccan_verified_emails', JSON.stringify(verifiedEmails));
      console.log('Email saved to localStorage for future visits');

      // Move to first-question phase (NOT active yet)
      setPhase('first-question');
      setTimeRemaining(120); // Still 2 minutes
      setTimerActive(true);

      const firstName = userInfo.name.split(' ')[0];
      setMessages(prev => [...prev, {
        id: Date.now(),
        text: `Perfect! You're all verified, ${firstName}. What would you like to know?`,
        sender: 'bot',
        timestamp: new Date().toISOString()
      }]);

    } catch (error) {
      console.error('Verification error:', error);
      setVerificationError('Network error. Please try again.');
    }
  };

  const handleFirstQuestion = async (e) => {
    e.preventDefault();

    if (!inputMessage.trim()) return;

    const userMessage = {
      id: Date.now(),
      text: inputMessage,
      sender: 'user',
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    const messageToSend = inputMessage;
    setInputMessage('');

    // Send to AI - if successful, START 10-minute session
    await sendToAI(messageToSend, true); // true = first question
  };

  const sendToAI = async (message, isFirstQuestion = false) => {
    const newMessageCount = sessionMessageCount + 1;

    if (newMessageCount > 10) {
      handleSessionEnd('message_limit');
      return;
    }

    const timeSnapshot = timeRemaining;
    setIsTyping(true);

    try {
      const response = await fetch('/api/chatbot', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: message,
          conversationHistory: messages,
          userInfo: userInfo
        })
      });

      if (!response.ok) {
        throw new Error('Failed to get response');
      }

      const data = await response.json();

      let cleanText = data.reply
        .replace(/\*\*/g, '')
        .replace(/\*/g, '')
        .replace(/#{1,6}\s/g, '')
        .replace(/`{1,3}/g, '');

      // Backend tells us if AI redirected (not actually answering the question)
      const isRedirect = data.isRedirect === true;

      // Add warnings for active session BEFORE creating bot message
      if (phase === 'active') {
        if (timeSnapshot > 150 && timeSnapshot <= 180 && !warningsShownRef.current.has('time3')) {
          cleanText += '\n\n⏱️ You have three more minutes to chat with me.';
          warningsShownRef.current.add('time3');
        } else if (timeSnapshot > 90 && timeSnapshot <= 150 && !warningsShownRef.current.has('time2')) {
          cleanText += '\n\n⏱️ You have two more minutes to chat with me.';
          warningsShownRef.current.add('time2');
        } else if (timeSnapshot > 30 && timeSnapshot <= 90 && !warningsShownRef.current.has('time1')) {
          cleanText += '\n\n⏱️ You have one more minute to chat with me.';
          warningsShownRef.current.add('time1');
        }

        if (newMessageCount === 7 && !warningsShownRef.current.has('msg7')) {
          cleanText += '\n\n💬 You have three more responses from me.';
          warningsShownRef.current.add('msg7');
        } else if (newMessageCount === 8 && !warningsShownRef.current.has('msg8')) {
          cleanText += '\n\n💬 You have two more responses from me.';
          warningsShownRef.current.add('msg8');
        } else if (newMessageCount === 9 && !warningsShownRef.current.has('msg9')) {
          cleanText += '\n\n💬 You have one more response from me.';
          warningsShownRef.current.add('msg9');
        }
      }

      // Create bot message
      const botMessage = {
        id: Date.now() + 1,
        text: cleanText,
        sender: 'bot',
        timestamp: new Date().toISOString()
      };

      // Add message to state
      setMessages(prev => [...prev, botMessage]);

      // If we're in first-question phase AND AI is NOT redirecting, start 10-minute session
      if (phase === 'first-question' && !isRedirect) {
        const now = Date.now();
        sessionStartTimeRef.current = now;

        // Set firstSessionStartTime ONLY if not already set (first time starting a session)
        if (!firstSessionStartTimeRef.current) {
          firstSessionStartTimeRef.current = now;
        }

        setTimeRemaining(600);
        setSessionMessageCount(0);
        setPhase('active');
        warningsShownRef.current.clear();

        const updatedMessages = [...messages, botMessage];
        saveSessionData(userInfo.email, {
          sessionStartTime: sessionStartTimeRef.current,
          firstSessionStartTime: firstSessionStartTimeRef.current,
          messageCount: 1,
          timeRemaining: 600,
          messages: updatedMessages
        });
        console.log('10-minute session started after valid business question');
      } else if (phase === 'first-question' && isRedirect) {
        // AI redirected - stay in first-question phase, don't start 10-min session
        console.log('AI redirected user to business topics - staying in first-question phase');
        // Keep the 2-minute timer running, don't increment message count
      } else if (phase === 'active') {
        // Save session data for active sessions
        setSessionMessageCount(newMessageCount);

        if (userInfo.email && sessionStartTimeRef.current) {
          const updatedMessages = [...messages, botMessage];
          saveSessionData(userInfo.email, {
            sessionStartTime: sessionStartTimeRef.current,
            firstSessionStartTime: firstSessionStartTimeRef.current,
            messageCount: newMessageCount,
            timeRemaining: timeRemaining,
            messages: updatedMessages
          });
        }

        if (newMessageCount === 10) {
          setTimeout(() => {
            handleSessionEnd('message_limit');
          }, 2000);
        }
      }

    } catch (error) {
      console.error('AI error:', error);

      const fallbackMessage = {
        id: Date.now() + 1,
        text: "I apologize, but I'm having trouble connecting right now. Please feel free to contact us directly at +64 9 886 4638.",
        sender: 'bot',
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, fallbackMessage]);

      // CRITICAL: Don't change phase or timer when API fails
      // User should stay in first-question phase with 2-minute timer
    } finally {
      setIsTyping(false);

      // Re-focus input field after bot responds so user can type immediately
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  };

  const submitConversationToBackend = () => {
    if (!userInfo.email) return;

    // Build conversation thread
    const conversation = messages.map(msg => ({
      sender: msg.sender,
      text: msg.text,
      timestamp: msg.timestamp
    }));

    fetch('/api/chatbot/lead', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...userInfo,
        verified: true,
        conversation: conversation,
        sessionDuration: sessionMessageCount,
        timestamp: new Date().toISOString()
      })
    }).catch(err => console.error('Failed to submit conversation:', err));
  };

  const handleSessionEnd = (reason) => {
    setTimerActive(false);
    setPhase('timeout');

    const endMessage = {
      id: Date.now(),
      text: reason === 'message_limit'
        ? "You've reached the maximum number of responses for this session. Please chat with me again in an hour!"
        : "Your session time has expired. Please chat with me again in an hour!",
      sender: 'bot',
      timestamp: new Date().toISOString()
    };

    // Submit conversation to backend BEFORE showing end message
    submitConversationToBackend();

    if (userInfo.email) {
      const now = Date.now();

      // Save user data permanently
      saveUserData(userInfo.email, {
        name: userInfo.name,
        email: userInfo.email,
        company: userInfo.company,
        phone: userInfo.phone
      });

      // Clear session data but set cooldown
      saveSessionData(userInfo.email, {
        sessionStartTime: null,
        firstSessionStartTime: null,
        messageCount: 0,
        sessionEndTime: now,
        cooldownUntil: now + (60 * 60 * 1000),
        messages: []
      });
    }

    setTimeout(() => {
      setMessages(prev => [...prev, endMessage]);
      setTimeout(() => {
        scrollToBottom();
        setTimeout(() => {
          setIsOpen(false);
          setTimeout(() => {
            setMessages([]);
            setPhase('contact');
            setTimeRemaining(120);
            setTimerActive(false);
            setIsVerified(false);
            setVerificationCode('');
            setUserInfo({ name: '', email: '', company: '', phone: '' });
            setVerificationError('');
            setInputMessage('');
            setSessionMessageCount(0);
            sessionStartTimeRef.current = null;
            firstSessionStartTimeRef.current = null;
            warningsShownRef.current.clear();
          }, 300);
        }, 5000);
      }, 100);
    }, 100);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!inputMessage.trim() || !isVerified) return;

    const userMessage = {
      id: Date.now(),
      text: inputMessage,
      sender: 'user',
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    const messageToSend = inputMessage;
    setInputMessage('');

    await sendToAI(messageToSend, false);
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <>
      {!isOpen && (
        <button
          className="chatbot-trigger"
          onClick={toggleChat}
          aria-label="Open chat"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2ZM20 16H6L4 18V4H20V16Z"
              fill="white"
            />
            <path d="M7 9H17V11H7V9Z" fill="white"/>
            <path d="M7 12H14V14H7V12Z" fill="white"/>
          </svg>
          <span className="chatbot-badge">AI</span>
        </button>
      )}

      {isOpen && (
        <div className="chatbot-container">
          <div className="chatbot-header">
            <div className="chatbot-header-info">
              <div className="chatbot-avatar">
                <img src="/img/Logo_2x_TP.png" alt="Banba AI" />
              </div>
              <div className="chatbot-header-text">
                <h3>Banba</h3>
                <p className="chatbot-status">
                  <span className="status-dot"></span>
                  {isVerified ? 'Connected' : 'Online'}
                </p>
              </div>
            </div>
            <div className="chatbot-header-actions">
              {timerActive && (
                <div className="timer-display">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.2 3.2.8-1.3-4.5-2.7V7z" fill={timeRemaining <= 10 ? "#ff4444" : "#ffffff"}/>
                  </svg>
                  <span className={timeRemaining <= 10 ? 'timer-warning' : ''}>
                    {formatTime(timeRemaining)}
                  </span>
                </div>
              )}
              <button
                onClick={handleMinimize}
                className="chatbot-action-btn"
                aria-label="Minimize"
              >
                −
              </button>
              <button
                onClick={handleClose}
                className="chatbot-action-btn"
                aria-label="Close"
              >
                ×
              </button>
            </div>
          </div>

          <div className="chatbot-messages">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`message ${message.sender}`}
              >
                {message.sender === 'bot' && (
                  <div className="message-avatar">
                    <img src="/img/Logo_2x_TP.png" alt="Bot" />
                  </div>
                )}
                <div className="message-content">
                  <div className="message-bubble">
                    {message.text}
                  </div>
                </div>
              </div>
            ))}

            {phase === 'contact' && (
              <div className="inline-form-container">
                <form onSubmit={handleLeadCapture} className="inline-lead-form">
                  <input
                    ref={nameInputRef}
                    type="text"
                    placeholder="Your Name *"
                    value={userInfo.name}
                    onChange={(e) => setUserInfo({...userInfo, name: e.target.value})}
                    required
                    disabled={!timerActive}
                  />
                  <input
                    type="email"
                    placeholder="Work Email *"
                    value={userInfo.email}
                    onChange={(e) => setUserInfo({...userInfo, email: e.target.value})}
                    required
                    disabled={!timerActive}
                  />
                  <input
                    type="text"
                    placeholder="Company"
                    value={userInfo.company}
                    onChange={(e) => setUserInfo({...userInfo, company: e.target.value})}
                    disabled={!timerActive}
                  />
                  <div style={{position: 'relative'}}>
                    <input
                      type="tel"
                      placeholder="Phone (optional)"
                      value={userInfo.phone}
                      onChange={(e) => {
                        const value = e.target.value;
                        const filtered = value.replace(/[^+\d]/g, '');
                        const sanitized = filtered.charAt(0) === '+'
                          ? '+' + filtered.slice(1).replace(/\+/g, '')
                          : filtered.replace(/\+/g, '');
                        if (sanitized.length > 0 && sanitized.charAt(0) !== '+') {
                          setShowPhoneHint(true);
                          setTimeout(() => setShowPhoneHint(false), 2500);
                          return;
                        }
                        if (sanitized !== value) {
                          setShowPhoneHint(true);
                          setTimeout(() => setShowPhoneHint(false), 2500);
                        }
                        setUserInfo({...userInfo, phone: sanitized});
                      }}
                      disabled={!timerActive}
                    />
                    {showPhoneHint && (
                      <div style={{
                        position: 'absolute', top: '-38px', left: '0', right: '0',
                        backgroundColor: '#333', color: '#fff', fontSize: '12px',
                        padding: '6px 10px', borderRadius: '4px', textAlign: 'center',
                        zIndex: 10, whiteSpace: 'nowrap'
                      }}>
                        Only + followed by digits allowed
                        <div style={{
                          position: 'absolute', bottom: '-6px', left: '50%', marginLeft: '-6px',
                          width: 0, height: 0, borderLeft: '6px solid transparent',
                          borderRight: '6px solid transparent', borderTop: '6px solid #333'
                        }} />
                      </div>
                    )}
                  </div>

                  {verificationError && (
                    <div className="verification-error">
                      {verificationError}
                    </div>
                  )}

                  <button type="submit" className="inline-submit-btn" disabled={!timerActive}>
                    Continue
                  </button>

                  <p className="chatbot-legal-links">
                    By continuing, you agree to our{' '}
                    <a href="https://yuccan.tech/legal/terms-of-use.html" target="_blank" rel="noopener noreferrer">
                      Terms of Use
                    </a>{' '}
                    and{' '}
                    <a href="https://yuccan.tech/legal/privacy-policy.html" target="_blank" rel="noopener noreferrer">
                      Privacy Policy
                    </a>
                  </p>
                </form>
              </div>
            )}

            {phase === 'email-verify' && (
              <div className="inline-form-container">
                <form onSubmit={handleVerification} className="inline-lead-form">
                  <input
                    ref={verificationInputRef}
                    type="text"
                    placeholder="Enter 6-digit code"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    maxLength="6"
                    required
                    disabled={!timerActive}
                    style={{textAlign: 'center', fontSize: '18px', letterSpacing: '6px'}}
                  />

                  {verificationError && (
                    <div className="verification-error">
                      {verificationError}
                    </div>
                  )}

                  <button type="submit" className="inline-submit-btn" disabled={!timerActive}>
                    Verify
                  </button>

                  <button
                    type="button"
                    className="resend-code-btn-small"
                    onClick={() => handleLeadCapture({preventDefault: () => {}})}
                  >
                    Resend Code
                  </button>
                </form>
              </div>
            )}

            {isTyping && (
              <div className="message bot">
                <div className="message-avatar">
                  <img src="/img/Logo_2x_TP.png" alt="Bot" />
                </div>
                <div className="message-content">
                  <div className="message-bubble typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {(phase === 'first-question' || phase === 'active') && (
            <form onSubmit={phase === 'first-question' ? handleFirstQuestion : handleSendMessage} className="chatbot-input-container">
              <input
                ref={inputRef}
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder="Type your message..."
                className="chatbot-input"
                disabled={isTyping}
              />
              <button
                type="submit"
                className="chatbot-send-btn"
                disabled={isTyping || !inputMessage.trim()}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"
                    fill="currentColor"
                  />
                </svg>
              </button>
            </form>
          )}

          <div className="chatbot-footer">
            <p>Powered by Claude AI · Yuccan Technologies</p>
          </div>
        </div>
      )}
    </>
  );
};

export default Chatbot;
